import React, { useEffect, useState, useMemo } from "react";
import { useFirebaseStorage } from "utils/hooks/useFirebaseStorage";
import { convertQueueEdit, getRecordEdit, getRecordDetail, deleteRecordFile } from "api/convert";
import { v4 as uuidv4 } from "uuid";
import { useMachine } from "@xstate/react";
import { fetchMachine, FetchEventType } from "machines/fetchMachine";
import { sliceTableData } from "utils/common";
import { Table, Pagination, Breadcrumb, Button, Icon, Upload, Select } from "common-components";
import { Modal, Tabs, Image } from "antd";
import { getTime, format } from "date-fns";
import { BoxEditSolid } from "@onedesign/icon";
import openNotificationWithIcon from "utils/hooks/useNotification";
import {
	UiQuestionRevisePage,
	BtnGroup,
	UiPopupContainer,
	UiButtons,
	UiStatus,
	UiQuesDetail,
} from "./QuestionRevisePage.style";

/**
 * 試題修改
 */
const { Header, Body, Row, Item } = Table;

const breadcrumbList = [
	{
		oneClassIcon: <BoxEditSolid />,
		name: "試題修改",
		path: "/",
	},
];

const showCodeList = ["CORE_ACCOMPLISHMENT", "PERFORMANCE", "LEARN_CONTENT", "KNOWLEDGE"];
const ButtonGroup = ({ item, onDetailPopupHandle, onDeleteHandle, onDownloadHandle, onReSubmitHandle }) => {
	const [deletePopupState, setDeletePopupState] = useState(false);
	const [reSubmitLoading, setReSubmitLoading] = useState(false);
	const onConfirmHandle = () => {
		onDeleteHandle(item);
		setDeletePopupState(false);
	};

	const retryBtn = () => {
		return item.allowRetry ? (
			<Button.IconButton
				variant="green"
				full={false}
				iconName="restart_alt"
				type="outLined"
				loading={reSubmitLoading}
				onClick={async () => {
					setReSubmitLoading(true);
					await onReSubmitHandle(item);
					setReSubmitLoading(false);
				}}>
				重新轉檔
			</Button.IconButton>
		) : (
			""
		);
	};

	switch (item.status) {
		case "Finish":
			return (
				<BtnGroup>
					{retryBtn()}
					<Button.IconButton
						variant="orange"
						full={false}
						iconName="visibility"
						onClick={() => onDetailPopupHandle(item)}>
						看更多
					</Button.IconButton>
					<Button.Popconfirm
						title="確定要刪除嗎?"
						block={false}
						full={false}
						variant="red"
						onCancel={() => setDeletePopupState(false)}
						onConfirm={onConfirmHandle}
						onClick={() => setDeletePopupState(true)}
						visible={deletePopupState}>
						<Icon name="delete_forever" mr="2" />
						刪除
					</Button.Popconfirm>
				</BtnGroup>
			);
		case "Warning":
		case "ErrorID":
			return (
				<BtnGroup>
					{retryBtn()}
					<Button.IconButton variant="blue" full={false} iconName="save_alt" onClick={() => onDownloadHandle(item)}>
						下載
					</Button.IconButton>
					<Button.Popconfirm
						title="確定要刪除嗎?"
						block={false}
						full={false}
						variant="red"
						onCancel={() => setDeletePopupState(false)}
						onConfirm={onConfirmHandle}
						onClick={() => setDeletePopupState(true)}
						visible={deletePopupState}>
						<Icon name="delete_forever" mr="2" />
						刪除
					</Button.Popconfirm>
				</BtnGroup>
			);
		default:
			return (
				<BtnGroup>
					{retryBtn()}
					<Button.Popconfirm
						title="確定要刪除嗎?"
						block={false}
						full={false}
						variant="red"
						onCancel={() => setDeletePopupState(false)}
						onConfirm={onConfirmHandle}
						onClick={() => setDeletePopupState(true)}
						visible={deletePopupState}>
						<Icon name="delete_forever" mr="2" />
						刪除
					</Button.Popconfirm>
				</BtnGroup>
			);
	}
};
export const QuestionRevisePage = () => {
	const [detailPopup, setDetailPopup] = useState({
		visible: false,
	});

	const [isLoading, setIsLoading] = useState(false);

	const { upload } = useFirebaseStorage();

	const [uploadPopup, setUploadPopup] = useState(false);

	const [files, setFiles] = useState([]);

	const [detailPage, setDetailPage] = useState(1);

	const [pageState, setPageState] = useState({
		currentPage: 1,
		limit: 10,
	});
	const [bookTagPath, setBookTagPath] = useState("");

	const [bookTagIsListen, setBookTagIsListen] = useState(false);

	const [stateRecord, sendRecord] = useMachine(fetchMachine, {
		services: {
			fetchData: async () => {
				const res = await getRecordEdit();
				const { records, isFinished } = res.data;
				return {
					records,
					isFinished,
				};
			},
			deleteData: async (_c, event) => await deleteRecordFile(event.fileUID),
		},
	});

	const { records, isFinished } = stateRecord.context.result || {};

	const [stateRecordDetail, sendRecordDetail] = useMachine(fetchMachine, {
		services: {
			fetchData: async (_c, event) => {
				const { data } = await getRecordDetail(event.eduSubject, event.fileUID);
				return {
					detailList: data,
				};
			},
		},
	});

	const { detailList } = stateRecordDetail.context.result || {};

	const targetDetail = useMemo(() => {
		if (!detailList) return {};
		return detailList[detailPage - 1];
	}, [detailPage, detailList]);

	const targetSelectOptions = useMemo(() => {
		let result = [];
		if (Object.keys(targetDetail).length === 0 || !targetDetail.detailImage) return result;

		result =
			targetDetail?.detailImage.map((item) => ({
				name: item.name,
				value: item.path,
				isListen: false,
			})) || [];
		if (targetDetail.soundtracks) {
			const listenList = targetDetail.soundtracks.map((item) => ({
				name: item.name,
				value: item.path,
				isListen: true,
			}));
			result = result.concat(listenList);
		}
		return result;
	}, [targetDetail]);

	const toggleDetailVisible = (item) => {
		if (!detailPopup.visible) {
			setDetailPage(1);
			setBookTagPath("");
			setBookTagIsListen(false);
			const { eduSubject, uid } = item;
			sendRecordDetail(FetchEventType.Fetch, {
				eduSubject,
				fileUID: uid,
			});
		}
		setDetailPopup({
			...detailPopup,
			visible: !detailPopup.visible,
		});
	};

	const toggleUploadPopupVisible = () => {
		setUploadPopup(!uploadPopup);
		setFiles([]);
	};

	const changeFileList = (value) => {
		setFiles(value);
	};
	const submitFiles = async () => {
		if (files.length === 0) return;
		setIsLoading(true);
		await Promise.all(
			files.map(async (file) => {
				const uid = uuidv4();
				const res = await upload(`convert/${uid}_${file.name}`, file);
				const payload = {
					fileUrl: res.url,
					fileName: file.name,
					uid,
				};
				await doConvertQueue(payload);
			}),
		);
		setUploadPopup(false);
		setFiles([]);
		sendRecord(FetchEventType.Fetch);
		setIsLoading(false);
	};

	const deleteHandle = (item) => {
		sendRecord(FetchEventType.Delete, {
			fileUID: item.uid,
		});
	};

	const downloadHandle = (item) => {
		const { failedUrl, fileName } = item;
		const link = document.createElement("a");
		link.href = failedUrl;
		link.target = "_blank";
		link.download = fileName;
		link.click();
	};

	const doConvertQueue = async (payload) => {
		const { isSuccess, message } = await convertQueueEdit(payload);
		if (!isSuccess) openNotificationWithIcon("error", message);
	};

	const onReSubmitHandle = async (item) => {
		const { fileUrl, fileName, uid, specUID } = item;
		const payload = {
			fileUrl,
			fileName,
			uid,
		};
		await doConvertQueue(payload);
	};
	const onDetailPageChange = (currentPage) => {
		setDetailPage(currentPage);
	};

	const pageChange = (currentPage, pageSize) => {
		setPageState({
			...pageState,
			currentPage,
			limit: pageSize,
		});
	};
	const uploadStatusClassName = (status) => {
		switch (status) {
			case "Finish":
				return "";
			case "Checking":
			case "Waiting":
			case "Pending":
				return "loading";
			default:
				return "error";
		}
	};

	const onSelectBookTag = (path) => {
		const isListen = targetSelectOptions.find((item) => item.value === path).isListen;
		setBookTagPath(path);
		setBookTagIsListen(isListen);
	};

	useEffect(() => {
		sendRecord(FetchEventType.Fetch);
	}, []);
	useEffect(() => {
		if (isFinished === undefined) return;
		const interval = setInterval(() => {
			sendRecord(FetchEventType.Fetch);
		}, 1000 * 30);
		if (isFinished) {
			clearInterval(interval);
		}
		return () => clearInterval(interval);
	}, [isFinished]);

	useEffect(() => {
		if (targetSelectOptions.length === 0) return;
		setBookTagPath(targetSelectOptions[0].value);
	}, [targetSelectOptions]);
	return (
		<>
			<UiQuestionRevisePage>
				<div className="flexStartBox">
					<Breadcrumb data={breadcrumbList} />
				</div>
				<div className="flexEndBox">
					<Button.IconButton variant="blue" full={false} iconName="cloud_upload" onClick={toggleUploadPopupVisible}>
						上傳試題
					</Button.IconButton>
				</div>

				<Modal
					width={600}
					visible={uploadPopup}
					onCancel={toggleUploadPopupVisible}
					onOk={submitFiles}
					title="上傳試題"
					footer={
						<UiButtons>
							<Button variant="white" full={false} onClick={toggleUploadPopupVisible} Key="cancel" loading={isLoading}>
								取消
							</Button>
							<Button
								variant="orange"
								full={false}
								onClick={submitFiles}
								Key="ok"
								loading={isLoading}
								disabled={files.length === 0}>
								確認
							</Button>
						</UiButtons>
					}>
					<Upload
						fileList={files}
						multiple={true}
						onChange={changeFileList}
						prompt={
							<>
								僅限上傳20個Microsoft Word 檔<br />
								(副檔名 : .doc/.docx)
							</>
						}
						acceptFileType=".doc,.docx"
					/>
				</Modal>
			</UiQuestionRevisePage>
			<Table margin="3">
				<Header>
					<Row>
						<Item flex="2">檔案名稱</Item>
						<Item>檔案狀態</Item>
						<Item>日期</Item>
						<Item>上傳者</Item>
						<Item flex="2.5">操作</Item>
					</Row>
				</Header>
				<Body>
					{records &&
						sliceTableData(records, pageState.currentPage, pageState.limit).map((item, index) => {
							return (
								<Row key={index}>
									<Item flex="2">{item.fileName}</Item>
									<Item>
										<UiStatus className={uploadStatusClassName(item.status)}>{item.message}</UiStatus>
									</Item>
									<Item>{format(getTime(new Date(item.systemTime)), "yyyy-MM-dd HH:mm")}</Item>
									<Item>{item.userName}</Item>
									<Item flex="2.5">
										<ButtonGroup
											item={item}
											onDetailPopupHandle={toggleDetailVisible}
											onDeleteHandle={deleteHandle}
											onDownloadHandle={downloadHandle}
											onReSubmitHandle={onReSubmitHandle}
										/>
									</Item>
								</Row>
							);
						})}
				</Body>
			</Table>
			<Pagination
				total={records?.length || 0}
				defaultCurrent={1}
				current={pageState.currentPage}
				onChange={pageChange}
				defaultPageSize={pageState.limit}
			/>
			<UiQuesDetail id="quesDetail">
				<Modal
					visible={detailPopup.visible}
					onCancel={toggleDetailVisible}
					getContainer={document.getElementById("quesDetail")}
					width="75%"
					footer={
						<Pagination
							textcolor="#000"
							total={detailList?.length || 0}
							defaultPageSize={1}
							variant="white"
							current={detailPage}
							onChange={onDetailPageChange}
						/>
					}>
					<UiPopupContainer id="quesTab">
						<Tabs
							defaultActiveKey="1"
							tabBarExtraContent={
								<>
									{targetDetail?.questionPdf ? (
										<Button.IconButton
											variant="blue"
											type="outLined"
											iconName="picture_as_pdf"
											onClick={() => {
												window.open(targetDetail.questionPdf, "_blank");
											}}>
											PDF
										</Button.IconButton>
									) : (
										""
									)}
								</>
							}>
							<Tabs.TabPane
								tab={
									<>
										<Icon name="visibility" />
										檢視更多
									</>
								}
								key="1">
								<div className="container">
									<div className="content">
										<div className="title">題目</div>
										<div className="img">
											<Image
												src={targetDetail?.questionImage || "error"}
												preview={{
													getContainer: document.getElementById("quesTab"),
												}}
												fallback="data:image/png;base64,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"
											/>
										</div>
										<ul className="info">
											<li>
												入庫時間:{" "}
												{targetDetail?.createTime && format(new Date(targetDetail.createTime), "yyyy-MM-dd HH:mm")}
											</li>
											<li>最後修改時間: {targetDetail?.updateTime}</li>
										</ul>
									</div>
									<div className="content">
										<div className="title">基本資料</div>
										<div className="meta">
											編號:
											<span className="metaContent">{targetDetail?.uid}</span>
										</div>
										{targetDetail?.metadata &&
											targetDetail.metadata.map((meta, index) => {
												return (
													<div className="meta" key={index}>
														{`${meta.name}: `}
														{meta.content.map((content, contentIndex) => {
															return (
																<span className="metaContent" key={contentIndex}>
																	{new Set(showCodeList).has(meta.code) ? content.code : content.name}
																</span>
															);
														})}
													</div>
												);
											})}
									</div>
								</div>
							</Tabs.TabPane>
							<Tabs.TabPane
								tab={
									<>
										<Icon name="widgets" />
										解析狀態
									</>
								}
								key="2">
								<div className="container">
									<div className="content">
										<div className="title">題目</div>
										<div className="bookTag">
											<Select
												label="解析書籤名稱:"
												options={targetSelectOptions}
												value={bookTagPath}
												onChange={(val) => {
													onSelectBookTag(val);
												}}
											/>
										</div>
										<div className="img">
											{bookTagIsListen ? (
												<audio controls src={bookTagPath || "error"}>
													Your browser does not support the
													<code>audio</code> element.
												</audio>
											) : (
												<Image
													src={bookTagPath || "error"}
													preview={{
														getContainer: document.getElementById("quesTab"),
													}}
													fallback="data:image/png;base64,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"
												/>
											)}
										</div>
									</div>
									<div className="content">
										<div className="title">基本資料</div>
										<div className="meta">
											編號:
											<span className="metaContent">{targetDetail?.fileUID}</span>
										</div>
										{targetDetail?.metadata &&
											targetDetail.metadata.map((meta, index) => {
												return (
													<div className="meta" key={index}>
														{`${meta.name}: `}
														{meta.content.map((content, contentIndex) => {
															return (
																<span className="metaContent" key={contentIndex}>
																	{new Set(showCodeList).has(meta.code) ? content.code : content.name}
																</span>
															);
														})}
													</div>
												);
											})}
									</div>
								</div>
							</Tabs.TabPane>
						</Tabs>
					</UiPopupContainer>
				</Modal>
			</UiQuesDetail>
		</>
	);
};

QuestionRevisePage.propTypes = {};
